import React, {PureComponent, Fragment} from 'react'
import styles from './styles'
import Typography from '@material-ui/core/Typography'
import {
  FormContext,
  TimezonePicker,
  DateTimePicker,
  ReadOnly,
  AutoSuggestField,
  MultiSelect,
  OptionOrTextField,
  TextField,
  MemberTitle, CurrencySelect, OptionalInput, YesNoRadio, LinkButton
} from 'components'
import {Button, IconButton} from 'components/OptimisedMui'
import Checkbox from 'components/styled/Checkbox'
import {DialogHandler, uuid} from 'utils'
import { InputDialog } from 'components/InputDialog'
import DeleteIcon from '@material-ui/icons/Delete'
import { paramScoped } from 'utils/ComponentUtils'
import { Authorization } from 'utils/AuthorizationUtils'
import {FileUploadField} from 'components/FileUploadField'
import { RichTextEditor } from 'components/RichTextEditor'
import {NumberField} from 'components/numbers'
import {humanize} from 'utils'
import Collapse from '@material-ui/core/Collapse'
import InputAdornment from '@material-ui/core/InputAdornment'

export class Project extends PureComponent {
  static defaultProps = {
    name: 'Deal',
    title: 'Deal information',
  }

  state = {}

  memberDialogHandler = new DialogHandler(this, 'memberDialog')

  handleAddMember = async() => {
    const {onChange, context} = this.props
    const title = await this.memberDialogHandler.show()
    if (title) {
      onChange({...context, teamMembers: [...context.teamMembers, {title}]})
    }
  }

  handleDeleteMember = paramScoped(member => event => {
    const {onChange, context} = this.props
    onChange({...context, teamMembers: context.teamMembers.filter(m => m !== member)})
  })

  get teamMemberTitleOptions() {
    return ['CEO', 'CFO', 'CTO'].filter(title => !this.props.context.teamMembers.some(tm => tm.title === title))
  }

  handleAddFundUse = () => {
    const {context} = this.props
    this.props.onChange({...context, fundsUses: [...context.fundsUses, {category: '', percentage: '', key: uuid()}]})
  }

  handleDeleteFundUse = paramScoped(fundsUse => event => {
    const {onChange, context} = this.props
    onChange({...context, fundsUses: context.fundsUses.filter(f => f !== fundsUse)})
  })

  get dealVisible() {
    return ['published', 'active', 'closed'].includes(this.props.context.status)
  }

  get currencyPrefix() {
    return this.props.context.currency && this.props.context.currency.prefix
  }

  get currencyInputProps() {
    return this.getCurrencyInputProps(this.currencyPrefix)
  }

  getCurrencyInputProps = paramScoped(prefix => ({
    startAdornment: prefix ? <InputAdornment position="start">{prefix}</InputAdornment> : undefined
  }))

  deleteIcon = <DeleteIcon/>

  serviceLabelProvider = s => s.name
  accountLabelProvider = ({accountName}) => accountName
  currencyLabelProvider = c => c.code

  getDiscount = stage => {
    const bonus = Number(this.props.context[`${stage}Bonus`])
    const discount = 100 - 10000 / (bonus + 100)
    return Math.round(discount) || ''
  }

  onDiscountChange = paramScoped(stage => ({target: {value}}) => {
    let discount = Number(value)
    if (discount < 100) {
      discount = recurringPercentages[discount] || discount

      const bonus = 10000 / (100 - discount) - 100
      this.props.onChange({...this.props.context, [`${stage}Bonus`]: Math.round(bonus)})
    }
  })



  render = () => {
    const {onChange, context, errorContext, review, services, accountProvider, currencies} = this.props
    const [isUtility, isSecurity, isEquity, isProduct] = [context.typeOfIssuance === 'utility', context.typeOfIssuance === 'security', context.typeOfIssuance === 'equity', context.typeOfIssuance === 'product']

    return (
      <ReadOnly on={review}>
        <FormContext
          onChange={onChange}
          context={context}
          errorContext={errorContext}
          metaRef={this.props.metaRef}
        >

          <div className={styles.section}>
            <Typography variant='h6'>Basic information</Typography>
            {Authorization.admin &&
              <AutoSuggestField
                labelProvider={this.accountLabelProvider}
                suggestionProvider={accountProvider}
                member="issuerAccount"
                required
              />
            }
            <TextField member="name" label="Project Name (This will be displayed in the Deal Banner)"
                       meta={{errorLabel: 'Project Name'}} fullWidth required />
            <TextField member="tagLine" fullWidth required />
            <TextField member="shortDescription" label="Short Description of your project (2-3 sentences)"
                       meta={{errorLabel: "Short Description"}} fullWidth required multiline />
            <RichTextEditor member="description" label='Full Description (Displayed as "Investor Information")'
              meta={{errorLabel: "Full Description"}} fullWidth multiline required />
            <RichTextEditor
              member="problemBeingSolved"
              label={
                isProduct ?
                   `Eligibility` :
                   `Describe the problem your ${isUtility ? 'token' : 'project'} is solving.`
               }
               fullWidth
               multiline
               required
            />
            <RichTextEditor member="risks" label="What are the project risks" fullWidth multiline required />
          </div>

          <div className={styles.section}>
            <Typography variant='h6'>Branding</Typography>
            <Typography variant="body1" paragraph>
              The Project Logo {isEquity ? 'is' : 'and the Coin Emblem are'} used in the
              Deal Preview and Deal lists to provide visual references to your branding.
            </Typography>
            <Typography variant="body1">Supported file type: .PNG, .GIF or .JPG</Typography>
            <Typography variant="subtitle1">Logo including your project's name. Should be white on a transparent background.</Typography>
            <FileUploadField member="logo" type="file" required/>
            <Typography variant="subtitle1">Emblem without text, in color.</Typography>
            <FileUploadField member="emblem" type="file" required/>
          </div>

          {context.teamMembers.map((member, i) =>
            <div key={member.title} className={styles.section}>
              <Typography variant='h6'>{member.title}</Typography>
              {!member.default &&
                <IconButton className={styles.sectionDelete} onClick={this.handleDeleteMember(member)}>{this.deleteIcon}</IconButton>
              }
              <Fragment>
                <TextField member={`teamMembers[${i}].name`} errorMember={`teamMembers['${member.title}'].name`}
                           meta={{errorLabel: `${member.title} Name`}} fullWidth />
                <TextField member={`teamMembers[${i}].email`} errorMember={`teamMembers['${member.title}'].email`}
                           meta={{errorLabel: `${member.title} Email`}} />
                <TextField member={`teamMembers[${i}].url`} errorMember={`teamMembers['${member.title}'].url`}
                           meta={{errorLabel: `${member.title} LinkedIn`}} label="LinkedIn" />
                <Typography variant="body1">Photo (Supported file type: .PNG, .GIF or .JPG)</Typography>
                <FileUploadField member={`teamMembers[${i}].photo`} errorMember={`teamMembers['${member.title}'].photo`}
                                 type="file" meta={{errorLabel: `${member.title} Photo`}}/>
                <div className={styles.checkbox}><Checkbox member={`teamMembers[${i}].public`} errorMember={`teamMembers['${member.title}'].public`} type='checkbox'/>Public Contact</div>
              </Fragment>
              {(i === context.teamMembers.length - 1) &&
                <LinkButton className={styles.linkButton} onClick={this.handleAddMember}>Add another member</LinkButton>
              }
            </div>
          )}

          <InputDialog {...this.memberDialogHandler.props} title="Role of member" label="Title" valueRequired>
            <OptionOrTextField options={this.teamMemberTitleOptions} />
          </InputDialog>

          <div className={styles.section}>
            <Typography variant='h6'>Additional information</Typography>
            <TimezonePicker member="timezone" fullWidth />
            <TextField member="tokenSaleWebsite" label="Website URL" required/>
          </div>

          <div className={styles.section}>
            <Typography variant='h6'>Estimated Project Timeline</Typography>

            {isUtility &&
              <Fragment>
                <Typography variant="subtitle1">Project start date</Typography>
                <DateTimePicker member="kickOffDate" meta={{errorLabel: `Project Start Date`}} label="Start date" showDate />

                <Typography variant="subtitle1">Seed Round</Typography>
                <div className={styles('composite', 'date')}>
                  <DateTimePicker member="seedRoundStartDate" meta={{errorLabel: `Seed Round Start Date`}} label="Start date" showDate/>
                  <DateTimePicker member="seedRoundEndDate" meta={{errorLabel: `Seed Round End Date`}} label="End date" showDate />
                </div>
                <div className={styles('composite', 'bonus')}>
                  <TextField member="seedRoundBonus" label="Bonus" suffix="%" />
                  <TextField label="Discount" suffix="%" value={this.getDiscount('seedRound')} onChange={this.onDiscountChange('seedRound')} disabled={review} />
                </div>

                <Typography variant="subtitle1">Private/Early stage token sale</Typography>
                <div className={styles('composite', 'date')}>
                  <DateTimePicker member="earlyStageStartDate" meta={{errorLabel: `Early Stage Start Date`}} label="Start date" showDate />
                  <DateTimePicker member="earlyStageEndDate" meta={{errorLabel: `Early Stage End Date`}}  label="End date" showDate />
                </div>
                <div className={styles('composite', 'bonus')}>
                  <TextField member="earlyStageBonus" meta={{errorLabel: `Early Stage Bonus`}} label="Bonus" suffix="%" />
                  <TextField label="Discount" suffix="%" value={this.getDiscount('earlyStage')} onChange={this.onDiscountChange('earlyStage')} disabled={review} />
                </div>
              </Fragment>
            }

            <Typography variant="subtitle1">
              {isUtility && 'Pre-sale'}{isSecurity && 'Security token sale'}{isEquity && `${humanize(context.dealRound)}`}
            </Typography>
            <div className={styles('composite', 'date')}>
              <DateTimePicker member="startDate" meta={{errorLabel: `Pre-sale Start Date`}} label="Start date" showDate required/>
              <DateTimePicker member="endDate" meta={{errorLabel: `Pre-sale End Date`}}  label="End date" showDate required />
            </div>
            <div className={styles('composite', 'bonus')}>
              <TextField member="preSaleBonus" meta={{errorLabel: `Pre-sale Bonus`}}  label="Bonus" suffix="%" />
              <TextField label="Discount" suffix="%" value={this.getDiscount('preSale')} onChange={this.onDiscountChange('preSale')} disabled={review} />
            </div>

            {isUtility &&
              <Fragment>
                <Typography variant="subtitle1">Public sale</Typography>
                <div className={styles('composite', 'date')}>
                  <DateTimePicker member="publicSaleStartDate" meta={{errorLabel: `Public Sale Start Date`}}
                                  label="Start date" showDate/>
                  <DateTimePicker member="publicSaleEndDate" meta={{errorLabel: `Public Sale End Date`}} label="End date"
                                  showDate/>
                </div>
                <div className={styles('composite', 'bonus')}>
                  <TextField member="publicSaleBonus" meta={{errorLabel: `Public Sale Bonus`}} label="Bonus" suffix="%"/>
                  <TextField label="Discount" suffix="%" value={this.getDiscount('publicSale')}
                             onChange={this.onDiscountChange('publicSale')} disabled={review}/>
                </div>
              </Fragment>
            }

            {!isEquity &&
              <Fragment>
                <Typography variant="subtitle1">Any Follow-ons</Typography>
                <div className={styles('composite', 'date')}>
                  <DateTimePicker member="followOnStartDate" meta={{errorLabel: `Follow-on Start Date`}}  label="Start date" showDate />
                  <DateTimePicker member="followOnEndDate" meta={{errorLabel: `Follow-on End Date`}} label="End date" showDate />
                </div>
                {isUtility &&
                  <div className={styles('composite', 'bonus')}>
                    <TextField member="followOnBonus" meta={{errorLabel: `Follow-on Bonus`}} label="Bonus" suffix="%" />
                    <TextField label="Discount" suffix="%" value={this.getDiscount('followOn')} onChange={this.onDiscountChange('followOn')} disabled={review} />
                  </div>
                }
              </Fragment>
            }
          </div>

          <div className={styles.section}>
            <Typography variant='h6'>Services required<span className={styles.note}>(For {process.env.REACT_APP_NAME} Info only)</span></Typography>
            <MultiSelect className={styles.alignedColumns} member="services" options={services} labelProvider={this.serviceLabelProvider} meta={{prepopulated: true}} />
          </div>

          <div className={styles.section}>
            <MemberTitle member='currency'>What is the deal currency?</MemberTitle>
            <Typography variant="body1">
              This field sets the primary fiat or crypto currency that the deal is denominated in and that deal
              information is displayed in.
            </Typography>
            <CurrencySelect currencies={currencies} member="currencyId" name="currency1" required disabled={this.dealVisible}/>
          </div>

          <div className={styles.section}>
            <MemberTitle member='acceptedCurrencies' required>Which currencies do you propose to accept?</MemberTitle>
            {Authorization.admin &&
            <Typography variant="body1">
              For each, currency selected, you will be required to define the Deal Wallets and/or Bank Accounts in the
              Deal Admin menu, before the deal can be published and investor allocations can be made.
            </Typography>
            }
            <MultiSelect member="acceptedCurrencies" options={currencies} defaultOptions={[context.currency]} labelProvider={this.currencyLabelProvider} meta={{prepopulated: true}} />
          </div>

          <div className={styles.section}>
            <MemberTitle member="fundRaisingStarted" variant='h6'>Has fund raising already started?</MemberTitle>

            <OptionalInput member="fundRaisingStarted" name="fundRaisingStarted">
              <NumberField member="startingAmount" label="How much has already been raised?" fullWidth prefix={this.currencyPrefix}/>
            </OptionalInput>
          </div>

          <div className={styles.section}>
            <Typography variant='h6'>Target{isEquity && '/Valuation'}</Typography>

            {isEquity &&
              <NumberField member="targetMinimum" label="Minimum Raise Amount" fullWidth prefix={this.currencyPrefix}/>
            }
            <NumberField member="targetTotal" label={isEquity ? "Maximum Raise Amount" : "Fundraising target"} fullWidth prefix={this.currencyPrefix}/>

            {isUtility &&
              <Fragment>
                <NumberField member="targetSeed" label="Seed fundraising target" fullWidth prefix={this.currencyPrefix} />
                <NumberField member="targetPreSale" label="Pre-sale fundraising target" fullWidth prefix={this.currencyPrefix} />
                <NumberField member="targetMainSale" label="Main-sale fundraising target" fullWidth prefix={this.currencyPrefix} />
              </Fragment>
            }
            {!isUtility &&
              <Fragment>
                <NumberField member="premoneyValuation" label="Pre-money valuation" fullWidth prefix={this.currencyPrefix} />
                <NumberField member="equityPercentOffered" label="Equity percent offered" fullWidth suffix='%'/>
              </Fragment>
            }
          </div>

          <div className={styles.section}>
            <Typography variant='h6'>Individual Contribution</Typography>
            <NumberField member="minAllocationSize" label="Minimum amount" InputProps={this.currencyInputProps} />
            <NumberField member="maxAllocationSize" label="Maximum amount" InputProps={this.currencyInputProps} />
          </div>

          <div className={styles.section}>
            <MemberTitle required member='fundsUses' meta={{prepopulated: true}}>Proposed use of funds</MemberTitle>
            <Typography variant="subtitle1" style={{clear: 'both'}}>
              Please describe proposed Use of Funds (this is displayed in the deal banner)
            </Typography>
            <TextField style={{marginTop: 10}} member="useOfFunds" label="Description"
                       meta={{errorLabel: 'Use of Funds Description'}} fullWidth multiline required/>

            <Typography variant="subtitle1" style={{clear: 'both'}}>
              Upload infographic
            </Typography>
            <Typography variant="body1">
              Supported file type: .PNG, .GIF or .JPG
            </Typography>
            <FileUploadField member="fundsUseInfographic" label="Infographic"/>

            <Collapse in={!context.fundsUseInfographic}>
              <Typography variant="subtitle1" style={{clear: 'both'}}>
                Or list proposed use of funds (must add up to 100%)
              </Typography>
              {context.fundsUses.map((use, i) =>
                <Fragment key={use.key}>
                  <TextField member={`fundsUses[${i}].category`} errorMember={`fundsUses['${use.key}'].category`}
                             meta={{prepopulated: true, errorLabel: 'Use Of Funds Category'}}/>
                  <TextField member={`fundsUses[${i}].percentage`} errorMember={`fundsUses['${use.key}'].percentage`}
                             meta={{errorLabel: 'Use Of Funds Percentage'}} label="Percentage" prefix="%"/>
                  {!use.default &&
                  <IconButton className={styles.itemDelete}
                              onClick={this.handleDeleteFundUse(use)}>{this.deleteIcon}</IconButton>
                  }
                </Fragment>
              )}
              <TextField style={{float: 'right'}} value={context.fundsUses.reduce((sum, value) => sum + (Number(value.percentage) || 0), 0) || ''} disabled label="Total" prefix="%"/>
              <div className={styles.section}>
                <Button vaiant="contained" className={styles.addButton} onClick={this.handleAddFundUse} fullWidth>Add Another Use of Funds</Button>
              </div>
            </Collapse>
          </div>

          {!isEquity &&
            <div className={styles.section}>
              <Typography variant='h6'>Token Sale Rate ({context.ticker || 'Tokens'}/{context.currency ? context.currency.code : 'Coin'})</Typography>
              <NumberField member="baseTokenRate" label="Token rate" required />
            </div>
          }

          {!isEquity &&
            <div className={styles.section}>
              <Typography variant='h6'>Token information</Typography>
              <TextField member="tickerLongForm" label="Token name (long form)" fullWidth />
              <TextField member="tickerShortForm" label="Token name (short form)" fullWidth />
              <TextField member="ticker" label="Ticker symbol" fullWidth required={isUtility} />
              {isUtility &&
                <NumberField member="preSaleTokenAmount" label="What is the supply of tokens for the pre-sale?" fullWidth required/>
              }
              <TextField member="tokenType" label={
                <Fragment>
                  What standard will be used for smart contracts
                  {isUtility && ' (e.g. ERC20/REM/Waves/EOS etc.?)'}
                </Fragment>
              } fullWidth required />
            </div>
          }

          <div className={styles.section}>
            <Typography variant='h6'>After completion of the {isEquity ? 'equity raise' : 'token sale'}, do you plan any subsequent or secondary issuances?</Typography>
            <YesNoRadio member="subsequentIssuesPlanned" name="subsequentIssuesPlanned"/>
          </div>
        </FormContext>
      </ReadOnly>
    )
  }
}

// These are the most likely percentages that a user would expect to be treated as a fraction
const recurringPercentages = {33: 100/3, 67: 200/3}

export default Project